/* eslint-disable react/destructuring-assignment */

import { Filter } from "@axl-dev/school-sdk";
import { Select } from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLoc, useSdk } from "../lib/hooks";

export declare type ClickIdentifierType =
  | "gclid"
  | "fbclid"
  | "msclkid"
  | "ttclid";

export declare type ClickIdentifierSource = "conversion";

export class ClickIdentifierFilter extends Filter {
  constructor(filter?: Partial<ClickIdentifierFilter>) {
    super();
    if (filter) this.update(filter);
  }

  type: ClickIdentifierType;
  source: ClickIdentifierSource = "conversion";

  update(changes: Partial<ClickIdentifierFilter>) {
    super.update(changes);
  }
}

type Props = {
  filter: Partial<ClickIdentifierFilter>;
  value: string[];
  onChange: (value: string[]) => void;
};

type ValueType = {
  key?: string;
  label: React.ReactNode;
  value: string | number;
};

export const ClickIdentifierSelect: React.FC<Props> = (props) => {
  const sdk = useSdk();
  const [isFetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const loc = useLoc();

  const fetchOptions = useCallback(
    async (searchString: string) => {
      const filterToFetch = new ClickIdentifierFilter({
        source: "conversion",
        ...props.filter,
      });
      filterToFetch.update({ search: searchString });

      const clickIdentifiersApi = (
        sdk.api.partnership as {
          clickIdentifiers?: {
            fetch: (
              fields?: string,
              filter?: ClickIdentifierFilter,
            ) => Promise<{ body?: { items?: string[] } }>;
          };
        }
      ).clickIdentifiers;

      const result = clickIdentifiersApi
        ? await clickIdentifiersApi.fetch("", filterToFetch)
        : await sdk.api.getList(
            "/partnership/click-identifiers",
            "",
            filterToFetch,
          );

      return (result.body?.items ?? []).map((item: string) => ({
        key: item,
        label: item,
        value: item,
      }));
    },
    [props.filter, sdk.api],
  );

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, 300);
  }, [fetchOptions]);

  useEffect(() => {
    fetchOptions("");
  }, [fetchOptions]);

  return (
    <Select
      showSearch
      labelInValue
      loading={isFetching}
      filterOption={false}
      onSearch={debounceFetcher}
      placeholder={loc.word("ClickIdentifiersSelector.placeholder", {
        default: "Select click IDs",
      })}
      {...props}
      options={options}
    />
  );
};
